import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { CommandCenterLayout } from '@/components/command/CommandCenterLayout';
import { OperatorShortcuts } from '@/components/OperatorShortcuts';
import { AppErrorBoundary } from '@/components/AppErrorBoundary';
import { RouteAccessGuard } from '@/components/RouteAccessGuard';
import { MarketingDormant } from '@/components/MarketingDormant';
import { MARKETING_PAGE_ROUTES, resolveDefaultLanding } from '@/lib/navAccess';
import { MARKETING_UI_ENABLED } from '@/lib/featureFlags';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { PropertyProvider, useProperty } from '@/contexts/PropertyContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { buildLoginRedirectPath, resolvePostLoginRedirect } from '@/lib/authRedirect';

const Settings = lazy(() =>
  import('@/pages/settings').then((module) => ({ default: module.Settings })),
);
const AdminSettings = lazy(() =>
  import('@/pages/settings/AdminSettings').then((module) => ({ default: module.AdminSettings })),
);
const PricingBoundaries = lazy(() =>
  import('@/pages/settings/PricingBoundaries').then((module) => ({
    default: module.PricingBoundaries,
  })),
);
const OnboardingWizard = lazy(() =>
  import('@/pages/settings/OnboardingWizard').then((module) => ({
    default: module.OnboardingWizard,
  })),
);
const Events = lazy(() => import('@/pages/Events'));
const loadCommandCenter = () => import('@/pages/CommandCenter');
const CommandCenter = lazy(loadCommandCenter);
const Avant = lazy(() => import('@/pages/Avant'));
const FinanceDesk = lazy(() => import('@/pages/FinanceDesk'));
const FinanceDocumentDownload = lazy(() => import('@/pages/FinanceDocumentDownload'));
const FinanceDealSummary = lazy(() => import('@/pages/FinanceDealSummary'));
const FinanceCapitalAccounts = lazy(() => import('@/pages/FinanceCapitalAccounts'));
const ConciergeLeads = lazy(() => import('@/pages/ConciergeLeads'));
const loadIntelligence = () => import('@/pages/Intelligence');
const Intelligence = lazy(loadIntelligence);
const System = lazy(() => import('@/pages/System'));
const TodaysCalls = lazy(() => import('@/pages/TodaysCalls'));
const WorkQueuePage = lazy(() => import('@/pages/WorkQueue'));
const MarketingCommandCenter = lazy(() => import('@/pages/MarketingCommandCenter'));
const BrandDefenceApprovals = lazy(() => import('@/pages/BrandDefenceApprovals'));
const Login = lazy(() => import('@/pages/Login').then((module) => ({ default: module.Login })));
const MobilePage = lazy(() => import('@/pages/mobile'));
const UltimateAssistantMobile = lazy(
  () => import('@/pages/ultimateAssistantMobile/UltimateAssistantMobile'),
);
const AttentionNowMobile = lazy(() => import('@/pages/ultimateAssistantMobile/AttentionNowView'));
const DevEndpointSmokeTest = import.meta.env.DEV
  ? lazy(() => import('@/pages/dev/EndpointSmokeTest'))
  : null;
const DevTodaysCallsPlansPreview = import.meta.env.DEV
  ? lazy(() => import('@/pages/dev/TodaysCallsPlansPreview'))
  : null;

// Download the requested page while authentication initializes. Rendering and
// all data access still wait for AuthProvider and RouteAccessGuard below.
// A direct reload previously waited for /api/me before starting these chunks.
if (typeof window !== 'undefined') {
  const path = window.location.pathname.replace(/\/$/, '');
  const loadPage =
    path === '/command' || path === '/legacy'
      ? loadCommandCenter
      : path === '/intelligence'
        ? loadIntelligence
        : null;
  if (loadPage)
    void loadPage().catch(() => {
      /* The route error boundary handles a failed load. */
    });
}

function RateGridRedirect() {
  const location = useLocation();
  return (
    <Navigate to={{ pathname: '/command', search: location.search, hash: location.hash }} replace />
  );
}

function normalizePropertySlug(raw: string): string | null {
  const normalized = raw
    .toLowerCase()
    .trim()
    .replace(/-/g, '_')
    .replace(/[^a-z0-9_]/g, '');
  return normalized.length > 0 ? normalized : null;
}

function PropertyShortcutRedirect({ landing }: { landing: string }) {
  const { propertyId } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const normalized = propertyId ? normalizePropertySlug(propertyId) : null;
  if (normalized) {
    // Property IDs are slugs only; the active set comes from the property API.
    params.set('property', normalized);
  }

  const search = params.toString();
  return (
    <Navigate
      to={{ pathname: landing, search: search ? `?${search}` : '', hash: location.hash }}
      replace
    />
  );
}

function TabRedirect({ pathname, tab }: { pathname: string; tab?: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (tab) {
    params.set('tab', tab);
  }
  const search = params.toString();

  return (
    <Navigate to={{ pathname, search: search ? `?${search}` : '', hash: location.hash }} replace />
  );
}

function AppLoading() {
  useEffect(() => {
    const w = window as Window & { __initialLoadTraceMark?: (name: string) => void };
    if (import.meta.env.DEV && typeof w.__initialLoadTraceMark === 'function') {
      w.__initialLoadTraceMark('first_skeleton_shown');
    }
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col items-center justify-center gap-6">
      <img
        src="/silas-face.svg"
        alt="Silas"
        className="w-16 h-16 opacity-60"
        style={{
          animation: 'silas-pulse 2s ease-in-out infinite',
          filter: 'drop-shadow(0 0 20px rgba(196, 160, 105, 0.3))',
        }}
      />
      <div className="flex items-center gap-2 text-[var(--text-secondary)] text-sm tracking-widest uppercase">
        <span>Loading</span>
        <span className="flex gap-1">
          <span style={{ animation: 'dot-fade 1.4s ease-out infinite' }}>.</span>
          <span style={{ animation: 'dot-fade 1.4s ease-out 0.2s infinite' }}>.</span>
          <span style={{ animation: 'dot-fade 1.4s ease-out 0.4s infinite' }}>.</span>
        </span>
      </div>
      <style>{`
      @keyframes silas-pulse {
        0%, 100% { opacity: 0.4; transform: scale(1); }
        50% { opacity: 0.8; transform: scale(1.05); }
      }
      @keyframes dot-fade {
        0%, 80%, 100% { opacity: 0.3; }
        40% { opacity: 1; }
      }
      @media (prefers-reduced-motion: reduce) {
        [style*="silas-pulse"], [style*="dot-fade"] { animation: none !important; }
      }
    `}</style>
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  const { property, properties, isLoading, setPropertyById } = useProperty();
  const selectedProperty = property?.id ?? '';
  const availableProperties = properties.map(({ id, name, readOnly }) => ({ id, name, readOnly }));
  const decisionLandingEnabled = import.meta.env.VITE_ENABLE_DECISION_LANDING !== 'false';
  // Land people somewhere they can actually open. A marketing partner has no
  // DASHBOARD_VIEW, so the usual landing would bounce them straight into the
  // access guard on sign-in — a working account that looks broken.
  const defaultLanding = resolveDefaultLanding(user, decisionLandingEnabled);
  const location = useLocation();
  const isSuperAdmin = user?.role === 'super_admin';
  const isUltimateAssistantMobile = location.pathname.startsWith('/mobile/assistant');

  useEffect(() => {
    const w = window as Window & { __initialLoadTraceMark?: (name: string) => void };
    if (import.meta.env.DEV && typeof w.__initialLoadTraceMark === 'function') {
      w.__initialLoadTraceMark('first_route_render');
    }
  }, []);

  if (isLoading && !selectedProperty && !isUltimateAssistantMobile) {
    return <AppLoading />;
  }

  // Consolidated route handling - single Routes block for all paths
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <RouteAccessGuard>
      <Routes>
        {/* Default landing */}
        <Route
          index
          element={
            <Navigate
              to={{ pathname: defaultLanding, search: location.search, hash: location.hash }}
              replace
            />
          }
        />
        <Route path="/legacy" element={<CommandCenter />} />

        {/* Command Center main view (rate grid) - no layout wrapper */}
        <Route path="/command" element={<CommandCenter />} />
        <Route path="/rate-grid" element={<RateGridRedirect />} />

        {/* Legacy deep-link format: /property-soundry, /property-terra-lodge, etc. */}
        <Route
          path="/property-:propertyId"
          element={<PropertyShortcutRedirect landing={defaultLanding} />}
        />
        <Route
          path="/property/:propertyId"
          element={<PropertyShortcutRedirect landing={defaultLanding} />}
        />

        <Route
          path="/todays-calls"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <TodaysCalls property={selectedProperty} />
            </CommandCenterLayout>
          }
        />

        {/* All other routes use CommandCenterLayout with WorkQueue sidebar */}
        <Route
          path="/acquisitions"
          element={<TabRedirect pathname="/intelligence" tab="acquisitions" />}
        />
        <Route
          path="/marketing-approvals"
          element={<TabRedirect pathname="/workspace/queue" tab="approval" />}
        />
        {/*
          Marketing. Dormant since 2026-09-01 (MARKETING_UI_ENABLED in
          src/lib/featureFlags.ts). The command center stays live because its
          Web Analytics tab is the first-party measurement surface that keeps
          running; the page itself hides its other tabs. Brand Defence has no
          live surface, so it lands on a notice. The Work Queue aliases below
          keep redirecting into the queue, which hides its own marketing tabs.
        */}
        <Route
          path="/marketing-command-center"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <MarketingCommandCenter property={selectedProperty} />
            </CommandCenterLayout>
          }
        />
        <Route
          path="/marketing-analytics"
          element={<TabRedirect pathname="/marketing-command-center" tab="web-analytics" />}
        />
        {MARKETING_UI_ENABLED ? (
          <Route
            path="/brand-defence"
            element={
              <CommandCenterLayout
                property={selectedProperty}
                properties={availableProperties}
                onPropertyChange={setPropertyById}
              >
                <BrandDefenceApprovals property={selectedProperty} />
              </CommandCenterLayout>
            }
          />
        ) : (
          MARKETING_PAGE_ROUTES.map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <CommandCenterLayout
                  property={selectedProperty}
                  properties={availableProperties}
                  onPropertyChange={setPropertyById}
                >
                  <MarketingDormant />
                </CommandCenterLayout>
              }
            />
          ))
        )}
        <Route
          path="/engagement-review"
          element={<TabRedirect pathname="/workspace/queue" tab="engagement" />}
        />
        <Route
          path="/approvals"
          element={<TabRedirect pathname="/workspace/queue" tab="approval" />}
        />
        <Route
          path="/content-review"
          element={<TabRedirect pathname="/workspace/queue" tab="content" />}
        />
        <Route
          path="/organic-content"
          element={<TabRedirect pathname="/workspace/queue" tab="content" />}
        />
        <Route
          path="/publishing-ops"
          element={<TabRedirect pathname="/workspace/queue" tab="content" />}
        />
        <Route
          path="/organic-packages"
          element={<TabRedirect pathname="/workspace/queue" tab="content" />}
        />
        {/* Redirects from old routes */}
        <Route
          path="/channels"
          element={<TabRedirect pathname="/intelligence" tab="acquisitions" />}
        />
        <Route
          path="/acquisition"
          element={<TabRedirect pathname="/intelligence" tab="acquisitions" />}
        />
        <Route
          path="/intelligence"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <Intelligence property={selectedProperty} />
            </CommandCenterLayout>
          }
        />
        <Route path="/kpis" element={<TabRedirect pathname="/intelligence" tab="kpis" />} />
        <Route
          path="/site-analytics"
          element={<TabRedirect pathname="/intelligence" tab="site-analytics" />}
        />
        <Route
          path="/forecasting"
          element={<TabRedirect pathname="/intelligence" tab="forecasting" />}
        />
        <Route path="/insights" element={<TabRedirect pathname="/intelligence" tab="insights" />} />
        <Route
          path="/avant"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <Avant property={selectedProperty} />
            </CommandCenterLayout>
          }
        />
        <Route
          path="/finance"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <FinanceDesk />
            </CommandCenterLayout>
          }
        />
        <Route
          path="/finance/deals/:dealId"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <FinanceDesk />
            </CommandCenterLayout>
          }
        />
        <Route
          path="/finance/deals/:dealId/summary"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <FinanceDealSummary />
            </CommandCenterLayout>
          }
        />
        <Route
          path="/finance/deals/:dealId/capital-accounts"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <FinanceCapitalAccounts />
            </CommandCenterLayout>
          }
        />
        <Route
          path="/finance/documents/:documentId/download"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <FinanceDocumentDownload />
            </CommandCenterLayout>
          }
        />
        <Route
          path="/concierge-leads"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <ConciergeLeads />
            </CommandCenterLayout>
          }
        />
        <Route
          path="/system"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <System property={selectedProperty} />
            </CommandCenterLayout>
          }
        />
        <Route path="/control-tower" element={<TabRedirect pathname="/system" />} />
        <Route
          path="/control-tower/autonomy"
          element={
            isSuperAdmin ? (
              <TabRedirect pathname="/system" tab="autonomy" />
            ) : (
              <TabRedirect pathname="/system" />
            )
          }
        />
        <Route
          path="/control-tower/autopilot"
          element={
            isSuperAdmin ? (
              <TabRedirect pathname="/system" tab="autonomy" />
            ) : (
              <TabRedirect pathname="/system" />
            )
          }
        />
        <Route path="/system-map" element={<TabRedirect pathname="/system" tab="map" />} />
        <Route
          path="/workspace/queue"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <WorkQueuePage property={selectedProperty} />
            </CommandCenterLayout>
          }
        />
        <Route path="/data-ops" element={<TabRedirect pathname="/system" />} />
        <Route path="/api-smoke" element={<TabRedirect pathname="/system" />} />
        <Route path="/system-health" element={<TabRedirect pathname="/system" tab="map" />} />
        {DevEndpointSmokeTest && (
          <Route
            path="/dev/endpoints"
            element={
              <CommandCenterLayout
                property={selectedProperty}
                properties={availableProperties}
                onPropertyChange={setPropertyById}
              >
                <DevEndpointSmokeTest />
              </CommandCenterLayout>
            }
          />
        )}
        {DevTodaysCallsPlansPreview && (
          <Route
            path="/dev/todays-calls-plans"
            element={
              <CommandCenterLayout
                property={selectedProperty}
                properties={availableProperties}
                onPropertyChange={setPropertyById}
              >
                <DevTodaysCallsPlansPreview />
              </CommandCenterLayout>
            }
          />
        )}
        <Route
          path="/settings"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <Settings property={selectedProperty} />
            </CommandCenterLayout>
          }
        />
        <Route
          path="/admin/settings"
          element={
            isSuperAdmin ? (
              <CommandCenterLayout
                property={selectedProperty}
                properties={availableProperties}
                onPropertyChange={setPropertyById}
              >
                <AdminSettings property={selectedProperty} />
              </CommandCenterLayout>
            ) : (
              <Navigate to="/settings" replace />
            )
          }
        />
        <Route
          path="/settings/integrations/akia"
          element={<Navigate to="/admin/settings?tab=akia" replace />}
        />
        <Route
          path="/settings/pricing-boundaries"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <PricingBoundaries property={selectedProperty} />
            </CommandCenterLayout>
          }
        />
        <Route path="/settings/onboarding" element={<OnboardingWizard />} />
        <Route
          path="/events"
          element={
            <CommandCenterLayout
              property={selectedProperty}
              properties={availableProperties}
              onPropertyChange={setPropertyById}
            >
              <Events property={selectedProperty} />
            </CommandCenterLayout>
          }
        />
        <Route path="/learning" element={<TabRedirect pathname="/system" tab="learning" />} />
        <Route path="/labor" element={<TabRedirect pathname="/intelligence" tab="labor" />} />
        <Route
          path="/oracle-performance"
          element={<TabRedirect pathname="/system" tab="performance" />}
        />
        <Route path="/results" element={<TabRedirect pathname="/intelligence" tab="results" />} />

        {/* Mobile PWA — standalone, no CommandCenterLayout */}
        <Route path="/mobile" element={<MobilePage />} />
        <Route path="/mobile/assistant" element={<UltimateAssistantMobile />} />
        <Route path="/mobile/assistant/now" element={<AttentionNowMobile />} />
        <Route path="/mobile/assistant/card/:id" element={<AttentionNowMobile />} />
        <Route path="/mobile/assistant/event/:id" element={<AttentionNowMobile />} />
        <Route path="/mobile/assistant/attention/:id" element={<AttentionNowMobile />} />

        {/* Catchall redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </RouteAccessGuard>
      {location.pathname.startsWith('/mobile') ? null : <OperatorShortcuts />}
    </div>
  );
}

export function AuthenticatedApp() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <AppLoading />;
  }

  const postLoginRedirect = resolvePostLoginRedirect(location.search);
  const loginRedirectPath = buildLoginRedirectPath(location);

  return (
    <Suspense fallback={<AppLoading />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login/*"
          element={isAuthenticated ? <Navigate to={postLoginRedirect} replace /> : <Login />}
        />
        {/*
          The Ultimate Assistant mobile cockpit is intentionally PUBLIC.
          It is shipped as a TestFlight-only iOS app (private build,
          obscure Cloud Run URL), it currently serves fixture data only,
          and the real MCP bridge it would talk to enforces bearer auth
          server-side. Putting it behind Clerk only added a WKWebView
          hang on first launch (no shared cookie jar with Safari).
        */}
        <Route
          path="/mobile/assistant"
          element={
            <AppErrorBoundary label="cockpit-public">
              <UltimateAssistantMobile />
            </AppErrorBoundary>
          }
        />
        <Route
          path="/mobile/assistant/now"
          element={
            <AppErrorBoundary label="cockpit-attention-public">
              <AttentionNowMobile />
            </AppErrorBoundary>
          }
        />
        <Route
          path="/mobile/assistant/card/:id"
          element={
            <AppErrorBoundary label="cockpit-attention-public">
              <AttentionNowMobile />
            </AppErrorBoundary>
          }
        />
        <Route
          path="/mobile/assistant/event/:id"
          element={
            <AppErrorBoundary label="cockpit-attention-public">
              <AttentionNowMobile />
            </AppErrorBoundary>
          }
        />
        <Route
          path="/mobile/assistant/attention/:id"
          element={
            <AppErrorBoundary label="cockpit-attention-public">
              <AttentionNowMobile />
            </AppErrorBoundary>
          }
        />
        <Route
          path="/finance/documents/:documentId/download"
          element={
            isAuthenticated ? (
              <AppErrorBoundary label="finance-document-download">
                <FinanceDocumentDownload />
              </AppErrorBoundary>
            ) : (
              <Navigate to={loginRedirectPath} replace />
            )
          }
        />
        {/* Protected routes */}
        <Route
          path="/*"
          element={
            isAuthenticated ? (
              <PropertyProvider>
                <AppErrorBoundary label="routes">
                  <AppRoutes />
                </AppErrorBoundary>
              </PropertyProvider>
            ) : (
              <Navigate to={loginRedirectPath} replace />
            )
          }
        />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AppErrorBoundary label="app-root">
      <ThemeProvider>
        <AuthProvider>
          <AuthenticatedApp />
        </AuthProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}
